import { DUR, EASE, STAGGER } from "@aybuben/client/motion.ts";
import { formatTime, type LessonStep1 } from "@aybuben/client/screens/result.ts";
import type { ProgressKey } from "@aybuben/core/content/types.ts";
import { useEffect, useState, type ReactNode } from "react";

import { play } from "../audio.ts";
import { haptic } from "../haptics.ts";
import { useStrings } from "../i18n/index.tsx";
import { COL } from "../theme.ts";
import { Mascot } from "./Mascot.tsx";
import { easing, reducedMotion } from "./motion.ts";
import { usePeak } from "./peak.ts";
import { Btn, G } from "./primitives.tsx";
import { FaceRow } from "./tree/moment.tsx";

const out = easing(EASE.out);

/** A number that counts up from 0 over `slow`, after `delay`; settled at once under reduced motion. */
function CountUp({
	to,
	delay,
	format,
}: {
	to: number;
	delay: number;
	format: (n: number) => ReactNode;
}) {
	const [v, setV] = useState(() => (reducedMotion() ? to : 0));
	useEffect(() => {
		if (reducedMotion()) {
			setV(to);
			return;
		}
		let raf = 0;
		const start = performance.now() + delay;
		const tick = (now: number) => {
			const p = Math.min(1, Math.max(0, (now - start) / DUR.slow));
			setV(Math.round(to * out(p)));
			if (p < 1) raf = requestAnimationFrame(tick);
		};
		raf = requestAnimationFrame(tick);
		return () => cancelAnimationFrame(raf);
	}, [to, delay]);
	return <>{format(v)}</>;
}

const beat = (i: number) => ({ animationDelay: `${i * STAGGER}ms` });

interface TileProps {
	id: string;
	label: string;
	value: number;
	format: (n: number) => ReactNode;
	color: string;
	at: number;
}

const Tile = ({ id, label, value, format, color, at }: TileProps) => (
	<div
		data-testid={id}
		data-value={value}
		className="motion-enter flex flex-1 flex-col items-center rounded-2xl px-2 py-3"
		style={{ border: `2px solid ${color}`, background: COL.card, ...beat(at) }}
	>
		<div className="text-2xl font-extrabold" style={{ color }}>
			<CountUp to={value} delay={at * STAGGER} format={format} />
		</div>
		<div className="mt-0.5 text-xs font-semibold" style={{ color: COL.mute }}>
			{label}
		</div>
	</div>
);

interface LessonDoneProps {
	step: LessonStep1;
	onContinue: () => void;
	onReviewKeys: (keys: ProgressKey[]) => void;
	/** Once, as the step opens. */
	onShown?: () => void;
}

/** The lesson's own ending: how it went. The tree's news is the next step. */
export function LessonDone({ step, onContinue, onReviewKeys, onShown }: LessonDoneProps) {
	const t = useStrings();
	const peak = usePeak(() => haptic("reveal"));
	const tiles = step.bestRun === null ? 2 : 3;
	// The tiles land one after another; the pocket line follows the last.
	const pocketAt = tiles + 1;
	useEffect(() => {
		onShown?.();
		play("done");
		const timers: ReturnType<typeof setTimeout>[] = [];
		const at = (i: number, kind: Parameters<typeof haptic>[0]) =>
			timers.push(setTimeout(() => haptic(kind), i * STAGGER + DUR.slow));
		for (let i = 1; i <= tiles; i++) at(i, "tap");
		at(pocketAt, "correct");
		// A perfect lesson: a second `correct` a beat later (stage-up's pattern belongs to the tree).
		if (step.perfect) at(pocketAt + 1, "correct");
		return () => timers.forEach(clearTimeout);
	}, []);
	const p = step.pocket;
	const review = step.review;
	return (
		<div
			data-testid="lesson-done"
			className="flex min-h-screen flex-col items-center px-6 pt-14 text-center"
			style={{ paddingBottom: 140 }}
		>
			<Mascot state={step.mood} size={140} onPeak={peak} />
			<G
				size={46}
				className="motion-pop mt-4 block"
				style={{ color: COL.pom, fontWeight: 700, ...beat(1) }}
			>
				{step.cheer}
			</G>
			{step.perfectLine && (
				<p
					data-testid="done-flawless"
					className="motion-enter mt-3 text-lg font-extrabold"
					style={{ color: COL.lapisDark, ...beat(1) }}
				>
					{t("moment.perfect")}
				</p>
			)}
			{step.verdict && (
				<p
					className="motion-enter mt-3 text-sm font-extrabold"
					style={{ color: step.verdict.passed ? COL.green : COL.pomDark, ...beat(2) }}
				>
					{t(step.verdict.key)}
				</p>
			)}
			<div className="mt-6 flex w-full max-w-sm gap-3">
				<Tile
					id="done-first-try"
					label={step.perfect ? t("result.perfect") : t("result.firstTry")}
					value={step.acc}
					format={(n) => `${n}%`}
					color={COL.green}
					at={1}
				/>
				{step.bestRun !== null && (
					<Tile
						id="done-best-run"
						label={t("result.bestRun")}
						value={step.bestRun}
						format={String}
						color={COL.lapis}
						at={2}
					/>
				)}
				<Tile
					id="done-time"
					label={t("result.time")}
					value={step.durationMs}
					format={formatTime}
					color={COL.pom}
					at={tiles}
				/>
			</div>
			<p
				data-testid="done-pocket"
				data-value={p.n}
				className="motion-enter mt-6 text-lg font-extrabold"
				style={{ color: COL.ink, ...beat(pocketAt) }}
			>
				<CountUp
					to={p.n}
					delay={pocketAt * STAGGER}
					format={(n) =>
						p.kind === "letters" ? t("result.letters", { n, of: p.of }) : t("result.words", { n })
					}
				/>
				{p.kind === "words" && p.added > 0 && (
					<span className="ml-2 text-sm" style={{ color: COL.green }}>
						{t("result.wordsNew", { n: p.added })}
					</span>
				)}
			</p>
			{review && (
				<div
					className="motion-enter mt-6 w-full rounded-3xl p-4 text-left"
					style={{ background: COL.card, border: `2px solid ${COL.line}`, ...beat(pocketAt + 1) }}
				>
					<div className="text-sm font-semibold" style={{ color: COL.mute }}>
						{t("result.missedTitle")}
					</div>
					<FaceRow ids={review.ids} max={review.ids.length} />
					<div className="mt-3">
						<Btn onClick={() => onReviewKeys(review.keys)} color={COL.lapis}>
							{t("result.reviewThese")}
						</Btn>
					</div>
				</div>
			)}
			<div className="fixed inset-x-0 bottom-0 z-20" style={{ background: COL.bg }}>
				<div
					className="mx-auto max-w-md px-5 pt-4"
					style={{ paddingBottom: "max(20px, env(safe-area-inset-bottom))" }}
				>
					<Btn testId="done-continue" onClick={onContinue} color={COL.pom}>
						{t("result.continue")}
					</Btn>
				</div>
			</div>
		</div>
	);
}
